import { useMemo, useEffect } from 'react';
import type { ListIssueFieldTransformed } from '@atlassian/jira-business-fields/src/common/types.tsx';
import type { JiraFormattingArea } from '@atlassian/jira-relay/src/__generated__/saveRule_Mutation.graphql';
import { useFormatRulesStore } from '../format-rules-store/index.tsx';
import { getColorFormatRules } from './utils.tsx';

export const useGetColorFormatRule = ({
	formattingArea,
	fieldId, // only for cell rules
	issueId,
	issueFields,
}: {
	formattingArea: JiraFormattingArea;
	fieldId?: string;
	issueId?: string;
	issueFields?: ListIssueFieldTransformed[];
}) => {
	const [{ rowRules, cellRulesByFieldId, issueIdRowRuleMatched }, { setMatchedRowRule }] =
		useFormatRulesStore();

	const isCellArea = formattingArea === 'CELL';

	const formatRule = useMemo(() => {
		if (!issueFields || !issueId) return null;

		const rules = isCellArea && fieldId ? cellRulesByFieldId[fieldId] : rowRules;
		if (!rules?.length) return null;

		const rule = getColorFormatRules({
			rules,
			issueFields,
		});

		if (!rule) return null;
		if (isCellArea) {
			// If there is a row rule that is ranked before should not use the cell rule color
			const rowRule = issueIdRowRuleMatched[issueId];
			if (rowRule?.index != null && rowRule.index < rule.index) return null;
		}

		return rule;
	}, [
		cellRulesByFieldId,
		fieldId,
		isCellArea,
		issueFields,
		issueId,
		issueIdRowRuleMatched,
		rowRules,
	]);

	useEffect(() => {
		if (!isCellArea && formatRule && issueId) {
			// we store the matched row rule for the issue
			setMatchedRowRule({ rule: formatRule, issueId });
		}
	}, [formatRule, isCellArea, issueId, setMatchedRowRule]);

	return formatRule?.color || null;
};
